import type { IConsentForm, IConsentFormAutomation, IConsentFormSubmission } from '@torim-app/types';
import { adminUrlBuilder } from '../utils';

export class ConsentFormsApi {
  static async fetchConsentForms(): Promise<IConsentForm[]> {
    const url = adminUrlBuilder({}, 'consent-forms');
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'Failed to fetch consent forms';
    return await response.json();
  }

  static async fetchConsentFormById(formId: string): Promise<IConsentForm> {
    const url = adminUrlBuilder({ formId }, 'consent-forms/form');
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'Failed to fetch consent form';
    return await response.json();
  }

  static async createConsentForm(
    body: Pick<IConsentForm, 'title' | 'elements'>,
  ): Promise<IConsentForm> {
    const url = adminUrlBuilder({}, 'consent-forms');
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw 'Failed to create consent form';
    return await response.json();
  }

  static async updateConsentForm(
    formId: string,
    body: Pick<IConsentForm, 'title' | 'elements'>,
  ): Promise<IConsentForm> {
    const url = adminUrlBuilder({ formId }, 'consent-forms');
    const response = await fetch(url, {
      method: 'put',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw 'Failed to update consent form';
    return await response.json();
  }

  static async deleteConsentForm(formId: string): Promise<void> {
    const url = adminUrlBuilder({ formId }, 'consent-forms');
    const response = await fetch(url, {
      method: 'delete',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'Failed to delete consent form';
  }

  /**
   * Uploads one picked image and returns its hosted URL. Sent as multipart, so the photo
   * never travels inside the submission JSON.
   */
  static async uploadFormImage(
    formId: string,
    file: File,
    label: string,
    customerId?: string,
  ): Promise<string> {
    const url = adminUrlBuilder({ formId, label, ...(customerId ? { customerId } : {}) }, 'consent-forms/upload-image');
    const formData = new FormData();
    formData.append('image', file);
    // No content-type header — the browser sets the multipart boundary itself.
    const response = await fetch(url, { body: formData, method: 'post' });
    if (!response.ok) throw 'Failed to upload image';
    const { url: imageUrl } = await response.json();
    return imageUrl;
  }

  static async submitConsentForm(
    formId: string,
    body: {
      customerName: string;
      customerId?: string;
      data: Record<string, any>;
      signatureDataUrl?: string;
    },
  ): Promise<IConsentFormSubmission> {
    const url = adminUrlBuilder({ formId }, 'consent-forms/submit');
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw 'Failed to submit consent form';
    return await response.json();
  }

  static async fetchFormSubmissions(
    formId: string,
  ): Promise<IConsentFormSubmission[]> {
    const url = adminUrlBuilder({ formId }, 'consent-forms/submissions');
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'Failed to fetch form submissions';
    return await response.json();
  }

  static async fetchCustomerSubmissions(
    customerId: string,
  ): Promise<IConsentFormSubmission[]> {
    const url = adminUrlBuilder({ customerId }, 'consent-forms/customer-submissions');
    const response = await fetch(url, {
      method: 'get',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) throw 'Failed to fetch customer submissions';
    return await response.json();
  }

  static async generateFromTemplate(params: {
    templateType?: string;
    description?: string;
  }): Promise<IConsentForm> {
    const url = adminUrlBuilder({}, 'consent-forms/generate-from-template');
    const response = await fetch(url, {
      method: 'post',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(params),
    });
    if (!response.ok) throw 'Failed to generate consent form from template';
    return await response.json();
  }

  static async updateConsentFormAutomation(
    formId: string,
    automation: IConsentFormAutomation,
  ): Promise<IConsentForm> {
    const url = adminUrlBuilder({ formId }, 'consent-forms/automation');
    const response = await fetch(url, {
      method: 'put',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(automation),
    });
    if (!response.ok) throw 'Failed to update consent form automation';
    return await response.json();
  }
}
